import { useMemo } from "react";
import { useController, useFormContext } from "react-hook-form";
import { keyBy } from "lodash";

export function useWorkflowConditionController({
  name,
  parameters,
  operand,
  value,
  datePart,
  workflowPart,
}) {
  const workflowPartsMap = useMemo(() => {
    return keyBy(parameters, "name");
  }, [parameters]);

  const getDefaultValue = (datePartValue) => {
    if (datePartValue === "Month" || datePartValue === "Weekday") {
      return [];
    }

    return "";
  };

  const getDateParts = (workflowPartValue, datePartValue) => {
    return workflowPartsMap[workflowPartValue]?.dateParts?.filter((dp) => {
      return dp.name === datePartValue;
    })[0];
  };

  const hasDatePart = (workflowPartValue) => {
    return Boolean(workflowPartsMap[workflowPartValue]?.dateParts);
  };

  const getDefaultDatePart = (workflowPartValue) => {
    if (!workflowPartValue) return "";

    return hasDatePart(workflowPartValue)
      ? workflowPartsMap[workflowPartValue]?.dateParts[0].name
      : null;
  };

  const getDefaultOperand = (workflowPartValue) => {
    if (!workflowPartValue) return "";

    return hasDatePart(workflowPartValue)
      ? workflowPartsMap[workflowPartValue]?.dateParts[0]?.operands[0]
      : workflowPartsMap[workflowPartValue]?.operands[0];
  };

  const getOptionsSource = (workflowPartValue, datePartValue) => {
    return datePartValue
      ? getDateParts(workflowPartValue, datePartValue)
      : workflowPartsMap[workflowPartValue];
  };

  const getFieldType = (workflowPartValue, datePartValue) => {
    if (datePartValue) {
      return getDateParts(workflowPartValue, datePartValue).type;
    }

    return workflowPartsMap[workflowPartValue]?.type;
  };

  const { field } = useController({
    name: name,
    defaultValue: {
      workflowPart: workflowPart || parameters[0]?.name,
      datePart:
        datePart || getDefaultDatePart(workflowPart || parameters[0]?.name),
      operand:
        operand || getDefaultOperand(workflowPart || parameters[0]?.name),
    },
    rules: {
      validate: ({ workflowPart: wP, operand: o, datePart: d }) => {
        return hasDatePart(wP) ? Boolean(wP && d && o) : Boolean(wP && o);
      },
      required: {
        value: true,
        message: "Please select workflow parameters",
      },
    },
  });

  const { resetField, getValues } = useFormContext();

  return {
    workflowPartProps: {
      options: Object.keys(workflowPartsMap),
      value: field.value?.workflowPart || null,
      onBlur: field.onBlur,
      onChange: (_, v) => {
        field.onChange({
          ...field.value,
          workflowPart: workflowPartsMap[v]?.name,
          datePart: getDefaultDatePart(v),
          operand: getDefaultOperand(v),
        });

        resetField(`${name}.value`, {
          defaultValue: getDefaultValue(
            hasDatePart(v) ? workflowPartsMap[v]?.dateParts[0] : null
          ),
        });
      },
    },
    datePartProps: {
      options: workflowPartsMap[field.value?.workflowPart]?.dateParts?.map(
        (dp) => {
          return dp.name;
        }
      ),
      value: field.value?.datePart,
      onBlur: field.onBlur,
      onChange: (_, v) => {
        field.onChange({
          ...field.value,
          datePart: v,
          operand: getDateParts(field.value?.workflowPart, v).operands[0],
        });

        resetField(`${name}.value`, {
          defaultValue: getDefaultValue(v),
        });
      },
    },
    operandProps: {
      options: getOptionsSource(
        field.value?.workflowPart,
        field.value?.datePart
      )?.operands,
      value: field.value?.operand,
      onBlur: field.onBlur,
      onChange: (_, v) => {
        field.onChange({
          ...field.value,
          value: getValues(`${name}.value`),
          operand: v,
        });
      },
    },
    fieldProps: {
      id: `${name}.value`,
      name: "",
      options: getOptionsSource(
        field.value?.workflowPart,
        field.value?.datePart
      )?.options,
      type: getFieldType(field.value?.workflowPart, field.value?.datePart),
      value: value || getDefaultValue(field.value?.datePart),
    },
    hasDatePart: Boolean(field.value?.datePart),
    hasOperand: Boolean(field.value?.operand),
    hasField: Boolean(
      getFieldType(field.value?.workflowPart, field.value?.datePart)
    ),
  };
}

export default useWorkflowConditionController;
